import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Avatar } from '@tyro/core/avatar';
import type { IconPickerType } from '@tyro/core/icon-picker';
import { useTranslation } from '@tyro/i18n';
import { BlankFileIcon, VideoIcon } from '@tyro/icons';
import type { ReturnTypeFromUseDocumentationSpaces } from '../../api/spaces';

interface CategoryCardProps {
  space: ReturnTypeFromUseDocumentationSpaces;
  onSelect: (spaceId: number) => void;
}

export function CategoryCard({ space, onSelect }: CategoryCardProps) {
  const { t } = useTranslation(['documentation']);
  return (
    <Card
      sx={{
        height: '100%',
        alignItems: 'flex-start',
        transition: 'box-shadow 150ms ease, background-color 150ms ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <CardActionArea
        onClick={() => onSelect(space.id)}
        sx={{ height: '100%', p: 1.5 }}
      >
        <Stack direction="row" gap={1.25} alignItems="flex-start" height="100%">
          <Avatar
            name={space.name}
            icon={space.icon as IconPickerType}
            size={32}
            sx={{
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          />
          <Stack
            gap={0.25}
            sx={{
              minWidth: 0,
              flex: 1,
              height: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'text.primary' }}
              noWrap
            >
              {space.name}
            </Typography>
            {space.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {space.description}
              </Typography>
            )}
            <Stack
              direction="row"
              alignItems="center"
              gap={1.5}
              sx={{ color: 'text.disabled', mt: 1 }}
            >
              <Stack direction="row" alignItems="center" gap={0.5}>
                <BlankFileIcon sx={{ fontSize: 12 }} />
                <Typography variant="caption">
                  {t('documentation:reader.articlesCount', {
                    count: space.articleCount,
                  })}
                </Typography>
              </Stack>
              {space.videoCount > 0 && (
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <VideoIcon sx={{ fontSize: 12 }} />
                  <Typography variant="caption">
                    {t('documentation:reader.videosCount', {
                      count: space.videoCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
